/**
 * The task page's main column: the description, the latest submission note
 * while the task is under review, the own-completion notice, and the
 * append-only history.
 */

import type { ReactNode } from 'react';

import type { TaskView } from '../../api.js';
import {
  ActivityTimeline,
  EventTime,
  MarkdownView,
  useWorkspaceCopy,
} from '../../components.js';
import { InlineAlert } from '../../ui.js';
import { SectionHeading } from '../common.js';

export function TaskDescriptionAndHistory({
  view,
}: {
  readonly view: TaskView;
}): ReactNode {
  const copy = useWorkspaceCopy();
  const task = view.task;

  // The direct-completion event, if this task was finished by its own Owner.
  const completedDirectly = view.events.some(
    (event) => event.kind === 'completed_directly',
  );

  // The most recent submission's note — the one the reviewer is looking at.
  const latestSubmission = [...view.events]
    .reverse()
    .find((event) => event.kind === 'submitted' && event.note !== null);

  return (
    <div style={{ minWidth: 0 }}>
      <section
        aria-label={copy.task.description}
        style={{ marginBottom: 'var(--space-lg)' }}
      >
        <SectionHeading>{copy.task.description}</SectionHeading>
        <MarkdownView source={task.description} />
      </section>

      {/* The submission note, where a reviewer will actually read it:
              beside the work, while the work is under review. Earlier
              attempts stay in the timeline below, never overwritten. */}
      {task.status === 'IN_REVIEW' && latestSubmission !== undefined ? (
        <section
          aria-label={copy.task.submissionNoteHeading}
          style={{
            marginBottom: 'var(--space-lg)',
            padding: 'var(--space-md)',
            background: 'var(--surface-soft)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-card)',
          }}
        >
          <SectionHeading>{copy.task.submissionNoteHeading}</SectionHeading>
          <p
            style={{
              font: 'var(--type-caption)',
              color: 'var(--text-muted)',
              marginBottom: 8,
            }}
          >
            {copy.task.submissionNoteBy(latestSubmission.actor.displayName)}{' '}
            <EventTime iso={latestSubmission.occurredAt} />
          </p>
          <MarkdownView source={latestSubmission.note ?? ''} />
        </section>
      ) : null}

      {completedDirectly ? (
        <InlineAlert tone="info" role="status">
          {copy.task.ownCompletionNotice}
        </InlineAlert>
      ) : null}

      <section aria-label={copy.task.history}>
        <SectionHeading>{copy.task.history}</SectionHeading>
        <ActivityTimeline events={view.events} />
      </section>
    </div>
  );
}
